<script setup>
/**
 * UpdateAvailableToast — bandeau/toast « Mise à jour disponible ».
 *
 * S'affiche automatiquement lorsqu'un nouveau déploiement est détecté
 * (voir composables/useAppUpdate.js). L'utilisateur clique sur « Actualiser »
 * pour recharger et obtenir la nouvelle version.
 */
import { onMounted } from 'vue'
import Button from 'primevue/button'
import { useAppUpdate } from '@/composables/useAppUpdate'

const { updateAvailable, reloadApp, start } = useAppUpdate()

onMounted(() => start())
</script>

<template>
  <transition name="upd-slide">
    <div v-if="updateAvailable" class="app-update" role="status" aria-live="polite">
      <i class="pi pi-cloud-download app-update__icon"></i>
      <div class="app-update__text">
        <strong>Mise à jour disponible</strong>
        <span>Une nouvelle version de l'application est prête.</span>
      </div>
      <Button
        label="Actualiser"
        icon="pi pi-refresh"
        size="small"
        class="app-update__btn"
        @click="reloadApp"
      />
    </div>
  </transition>
</template>

<style scoped>
.app-update {
  position: fixed;
  right: var(--space-4, 16px);
  bottom: var(--space-4, 16px);
  z-index: 11000;
  display: flex;
  align-items: center;
  gap: var(--space-3, 12px);
  max-width: min(420px, calc(100vw - 32px));
  background: #fff;
  border: 1px solid var(--tertiary-200, #e6ddca);
  border-left: 4px solid var(--primary-500, #c3a363);
  border-radius: var(--border-radius-lg, 12px);
  box-shadow: var(--shadow-lg, 0 12px 32px rgba(33, 33, 33, 0.18));
  padding: var(--space-3, 12px) var(--space-4, 16px);
}
.app-update__icon {
  color: var(--primary-600, #b3924f);
  font-size: 22px;
  flex-shrink: 0;
}
.app-update__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 1px;
}
.app-update__text strong {
  color: var(--secondary, #212121);
  font-size: var(--text-sm, 0.875rem);
}
.app-update__text span {
  color: var(--text-secondary, #555);
  font-size: var(--text-xs, 0.75rem);
}
.app-update__btn {
  flex-shrink: 0;
  white-space: nowrap;
}

.upd-slide-enter-active,
.upd-slide-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.upd-slide-enter-from,
.upd-slide-leave-to {
  opacity: 0;
  transform: translateY(14px);
}

@media (max-width: 480px) {
  .app-update {
    left: var(--space-3, 12px);
    right: var(--space-3, 12px);
    bottom: var(--space-3, 12px);
    max-width: none;
    flex-wrap: wrap;
  }
  .app-update__btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
